import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import apiService from '../services/api';

interface Company { tenantId: number; name: string; }
interface Mapping { section: string; tenantId: number; }
interface MapsResponse { companies: Company[]; mappings: Mapping[]; unassignedSections: string[]; }

// Assigns each Yarn Dashboard warehouse section (A, B, C…) to a company. A company then
// sees only its assigned sections on the dashboard; unassigned sections show only to the
// reseller/super-admin. Changes take effect live — no "apply" step.
const SectionCompanyMapPanel: React.FC = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<MapsResponse>({
    queryKey: ['section-company-maps'],
    queryFn: () => apiService.get('/section-company-maps') as Promise<MapsResponse>,
  });

  const upsert = useMutation({
    mutationFn: (body: { section: string; tenantId: number }) => apiService.put('/section-company-maps', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['section-company-maps'] }); qc.invalidateQueries({ queryKey: ['yarn-dashboard'] }); toast.success('Saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to save'),
  });

  if (isLoading || !data) return <div className="p-4 text-sm text-slate-500">Loading section mappings…</div>;

  const current = new Map<string, number>(
    data.mappings.map((m: Mapping) => [m.section, m.tenantId] as [string, number])
  );
  const sections: string[] = [
    ...data.mappings.map((m: Mapping) => m.section),
    ...data.unassignedSections,
  ].filter((s: string, i: number, a: string[]) => a.indexOf(s) === i).sort();

  return (
    <div className="bg-white rounded-xl border border-[#E1E6EF] p-4 sm:p-5">
      <div className="mb-3">
        <h3 className="text-base font-bold text-[#0E131B]">Section → Company (Yarn Dashboard)</h3>
        <p className="text-xs text-slate-500">Each company sees only the sections you assign to it. Unassigned sections show only to you.</p>
      </div>
      <div className="space-y-1.5">
        {sections.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No sections found yet.</p>}
        {sections.map((section) => (
          <div key={section} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
            <span className="text-sm font-semibold text-[#0E131B]">Section {section}</span>
            <select
              value={current.get(section) ?? ''}
              onChange={(e) => upsert.mutate({ section, tenantId: Number(e.target.value) })}
              className="flex-shrink-0 w-44 sm:w-56 rounded-lg border border-[#E8EBF1] bg-[#F7F8FB] px-2.5 py-2 text-sm"
            >
              <option value="" disabled>Unassigned</option>
              {data.companies.map((c: Company) => (
                <option key={c.tenantId} value={c.tenantId}>{c.name}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SectionCompanyMapPanel;
